import { useQuery } from '@tanstack/react-query';
import { procurementApi } from '~/api/procurement';

export const purchaseOrderItemEditQueryKeys = {
    itemTypes: ['purchaseOrders', 'v2', 'itemTypes'],
    sourcingReasons: ['purchaseOrders', 'v2', 'sourcingReasons'],
    unpurchasedOutcomes: ['purchaseOrders', 'v2', 'unpurchasedOutcomes'],
    reasonCodes: ['purchaseOrders', 'v2', 'reasonCodes'],
};

export const usePurchaseOrderItemTypesQuery = ({ enabled = true } = {}) => {
    return useQuery({
        queryKey: purchaseOrderItemEditQueryKeys.itemTypes,
        enabled,
        queryFn: async () => {
            const response = await procurementApi.getPurchaseOrderItemTypes();
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch purchase types');
            }
            if (!Array.isArray(response?.data?.types)) {
                throw new Error('Unexpected response: purchase types is not an array');
            }
            const types = response.data.types;
            return types.filter(Boolean);
        },
        staleTime: 10 * 60_000,
        gcTime: 30 * 60_000,
    });
};

export const useSourcingReasonsQuery = ({ enabled = true } = {}) => {
    return useQuery({
        queryKey: purchaseOrderItemEditQueryKeys.sourcingReasons,
        enabled,
        queryFn: async () => {
            const response = await procurementApi.getSourcingReasons();
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch sourcing reasons');
            }
            if (!Array.isArray(response?.data?.reasons)) {
                throw new Error('Unexpected response: sourcing reasons is not an array');
            }
            const reasons = response.data.reasons;
            return reasons.map((r) => r?.name).filter(Boolean);
        },
        staleTime: 10 * 60_000,
        gcTime: 30 * 60_000,
    });
};

export const useUnpurchasedOutcomesQuery = ({ enabled = true } = {}) => {
    return useQuery({
        queryKey: purchaseOrderItemEditQueryKeys.unpurchasedOutcomes,
        enabled,
        queryFn: async () => {
            const response = await procurementApi.getUnpurchasedOutcomes();
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch unpurchased outcomes');
            }
            if (!Array.isArray(response?.data?.outcomes)) {
                throw new Error('Unexpected response: unpurchased outcomes is not an array');
            }
            const outcomes = response.data.outcomes;
            return outcomes.map((o) => o?.displayName).filter(Boolean);
        },
        staleTime: 10 * 60_000,
        gcTime: 30 * 60_000,
    });
};

export const usePurchaseOrderReasonCodesQuery = ({ enabled = true } = {}) => {
    return useQuery({
        queryKey: purchaseOrderItemEditQueryKeys.reasonCodes,
        enabled,
        queryFn: async () => {
            const response = await procurementApi.getPOUpdateReasonCodes();
            if (!response?.status) {
                throw new Error(response?.msg ?? 'Failed to fetch reason codes');
            }
            if (!Array.isArray(response?.data?.reasonCodes)) {
                throw new Error('Unexpected response: reason codes is not an array');
            }
            const codes = response.data.reasonCodes;
            return codes.filter(Boolean);
        },
        staleTime: 10 * 60_000,
        gcTime: 30 * 60_000,
    });
};
